import { useEffect, useMemo, useRef, useState } from 'react';
import type { GuaranteedServiceOrgSkuEntry, GuaranteedServiceSkuEntry, StoredGuardPolicy } from '@nadoutong/contracts';
import { GUARANTEED_CUSTOMERS_STALE_MS, useQuotaCustomers } from '../../../api/customerOwnership.ts';
import {
  fetchGuardConfigPropagation,
  usePutGuaranteedGuardConfig,
  type GuardConfigRow,
  type GuardWriteGate,
} from '../../../api/guaranteedGuardConfig.ts';
import { coerceErrorMessage } from '../../../components/Toast.tsx';
import { useSession } from '../../../hooks/useSession.tsx';
import { useT } from '../../../i18n/index.ts';
import { Button, ConfirmDialog, Drawer, FormField, Input, Select } from '../../../ui/index.ts';
import { isDirectOrgId } from '../../GuaranteedServicePage.tsx';
import { SearchDropdown } from '../../quotas/SearchDropdown.tsx';
import { RowWriteStatus } from '../RowWriteStatus.tsx';
import { watchShanghai } from '../shanghaiWatch.ts';
import { isRowBusy, type CnConfirmation, type RowWriteStatus as RowStatus } from '../writeStatus.ts';
import { GuardOrgTable } from './GuardOrgTable.tsx';
import { GuardRecoverTables } from './GuardRecoverTables.tsx';
import { GuardSignalRow } from './GuardSignalRow.tsx';
import {
  asStoredPolicy,
  changedGroups,
  draftIssues,
  draftOf,
  guardPutBody,
  previewPolicy,
  skuDraftOf,
  streamFromLegacy,
  type GuardDraft,
  type GuardDrawerScope,
} from './guardModel.ts';
import { GUARD_SIGNAL_VIEWS } from './guardSignals.ts';
import { useGuardStats } from './guardStats.ts';

const SKU_SCOPE = '__sku__';
const EMPTY: StoredGuardPolicy = { v: 2 };

function storedOf(row: GuardConfigRow | undefined): StoredGuardPolicy {
  return row === undefined ? EMPTY : asStoredPolicy(row.policy);
}

function storedKey(stored: unknown): string {
  return JSON.stringify(stored);
}

export function GuardConfigDrawer({
  skuRow,
  skuGuard,
  orgGuards,
  orgServiceRows,
  customerName,
  initialOrgId,
  writeGate,
  listReady,
  onClose,
}: {
  readonly skuRow: GuaranteedServiceSkuEntry;
  readonly skuGuard: GuardConfigRow | undefined;
  readonly orgGuards: readonly GuardConfigRow[];
  readonly orgServiceRows: readonly GuaranteedServiceOrgSkuEntry[];
  readonly customerName: (orgId: string) => string;
  readonly initialOrgId: string | null;
  readonly writeGate: GuardWriteGate;
  readonly listReady: boolean;
  readonly onClose: () => void;
}): JSX.Element {
  const t = useT();
  const { fetchImpl } = useSession();
  const { mutateAsync: putAsync } = usePutGuaranteedGuardConfig();
  const statsQuery = useGuardStats(skuRow.sku_id);
  const [orgId, setOrgId] = useState<string | null>(initialOrgId);
  const scope: GuardDrawerScope = orgId === null ? 'sku' : 'org';
  const skuStored = storedOf(skuGuard);
  const orgRow = orgId === null ? undefined : orgGuards.find((row) => row.org_id === orgId);
  const stored = scope === 'sku' ? skuStored : storedOf(orgRow);
  const legacy = typeof skuRow.no_response_cutoff_ms === 'number' ? skuRow.no_response_cutoff_ms : null;
  const freshDraft = (next: StoredGuardPolicy, nextScope: GuardDrawerScope, sku: StoredGuardPolicy): GuardDraft => draftOf({
    scope: nextScope, stored: next, skuStored: sku, legacyStreamCutoffMs: legacy,
  });
  const [baseline, setBaseline] = useState(() => stored);
  const [baselineDraft, setBaselineDraft] = useState<GuardDraft>(() => freshDraft(stored, scope, skuStored));
  const [draft, setDraft] = useState<GuardDraft>(() => freshDraft(stored, scope, skuStored));
  const [seenScope, setSeenScope] = useState(() => `${scope}:${orgId ?? ''}`);
  const [seenStored, setSeenStored] = useState(() => storedKey(stored));
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingScope, setPendingScope] = useState<string | null>(null);
  const [status, setStatus] = useState<RowStatus | undefined>(undefined);
  const pollers = useRef(new Map<string, AbortController>());
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    const active = pollers.current;
    return () => {
      mounted.current = false;
      for (const controller of active.values()) controller.abort();
      active.clear();
    };
  }, []);

  const scopeKey = `${scope}:${orgId ?? ''}`;
  const changed = changedGroups(draft, baselineDraft);
  const dirty = changed.length > 0;
  if (seenScope !== scopeKey) {
    setSeenScope(scopeKey);
    setSeenStored(storedKey(stored));
    setBaseline(stored);
    const fresh = freshDraft(stored, scope, skuStored);
    setBaselineDraft(fresh);
    setDraft(fresh);
    setStatus(undefined);
  } else if (seenStored !== storedKey(stored)) {
    // Someone else wrote this row: take the new stored value; keep edits only when there are some.
    setSeenStored(storedKey(stored));
    setBaseline(stored);
    const fresh = freshDraft(stored, scope, skuStored);
    setBaselineDraft(fresh);
    if (!dirty) setDraft(fresh);
  }

  const busy = isRowBusy(status);
  const issues = useMemo(() => draftIssues(draft, baselineDraft), [draft, baselineDraft]);
  const body = guardPutBody(draft, baselineDraft, baseline, { skuId: skuRow.sku_id, orgId });
  const preview = dirty && issues.size === 0 ? previewPolicy(baseline, body) : null;
  const previewError = preview !== null && !preview.ok ? preview.message : null;
  const gateClosed = !listReady || writeGate.open === false;
  const gateReason = writeGate.reason ?? '';
  const blocked = !dirty || issues.size > 0 || previewError !== null || busy || gateClosed;
  const orgOptions = orgChoices(orgGuards, orgServiceRows, customerName);
  const statsState = statsQuery.isError ? 'failed' : statsQuery.isSuccess ? 'ready' : 'loading';

  function applyScope(next: string): void {
    setOrgId(next === SKU_SCOPE ? null : next);
  }

  function requestClose(): void {
    if (dirty && !busy) {
      setPendingScope(null);
      setConfirmOpen(true);
      return;
    }
    onClose();
  }

  function switchScope(next: string): void {
    if (next === (orgId ?? SKU_SCOPE)) return;
    if (dirty && !busy) {
      setPendingScope(next);
      setConfirmOpen(true);
      return;
    }
    applyScope(next);
  }

  function setCn(_ids: readonly string[], cn: CnConfirmation): void {
    setStatus((current) => (current?.phase === 'saved' ? { ...current, cn } : current));
  }

  async function save(): Promise<void> {
    if (blocked) return;
    setStatus({ phase: 'saving', label: t('guaranteed.guard.saving', { n: changed.length }), pending: null });
    try {
      const response = await putAsync(body);
      const nextStored = asStoredPolicy(response.item.policy);
      const nextSku = scope === 'sku' ? nextStored : skuStored;
      const fresh = freshDraft(nextStored, scope, nextSku);
      setBaseline(nextStored);
      setSeenStored(storedKey(nextStored));
      setBaselineDraft(fresh);
      setDraft(fresh);
      setStatus({
        phase: 'saved',
        op: 'write',
        scope: scope === 'sku' ? 'sku' : 'org_sku',
        cache: response.cache,
        updatedAt: response.item.updated_at,
        receivedAtMs: Date.now(),
        cn: { kind: 'waiting', last: null },
      });
      watchShanghai({
        ids: [response.item.id],
        expectedUpdatedAt: response.item.updated_at,
        goal: 'match',
        read: fetchGuardConfigPropagation,
        fetchImpl,
        pollers: pollers.current,
        mounted,
        setCn,
      });
    } catch (error) {
      setStatus({ phase: 'error', message: coerceErrorMessage(error) });
    }
  }

  const disabled = busy || gateClosed;
  const skuView = scope === 'sku' && draft.scope === 'sku' ? draft : skuDraftOf(skuStored, legacy);

  return (
    <Drawer
      open
      onOpenChange={(open) => {
        if (!open) requestClose();
      }}
      title={t('guaranteed.guard.title', { sku: skuRow.sku_name })}
      description={t('guaranteed.guard.description')}
      width="2xl"
      closeLabel={t('guaranteed.guard.close')}
      testId="guaranteed-guard-drawer"
      footer={(
        <div className="flex w-full flex-col gap-2">
          <RowWriteStatus status={status} testId="guaranteed-guard-status" />
          {previewError === null ? null : (
            <p role="alert" className="m-0 break-words text-label text-bad" data-testid="guaranteed-guard-precheck">
              {t('guaranteed.guard.err.policy', { message: previewError })}
            </p>
          )}
          <div className="flex items-center justify-end gap-2">
            <span className="mr-auto text-label text-paper-low" data-testid="guaranteed-guard-changed">
              {dirty
                ? t('guaranteed.guard.changed', { n: changed.length })
                : t('guaranteed.guard.unchanged')}
            </span>
            <Button variant="ghost" onClick={requestClose} disabled={busy} data-testid="guaranteed-guard-close">
              {t('guaranteed.guard.close')}
            </Button>
            <Button
              variant="primary"
              onClick={() => void save()}
              disabled={blocked}
              loading={busy}
              data-testid="guaranteed-guard-save"
            >
              {t('guaranteed.guard.save')}
            </Button>
          </div>
        </div>
      )}
    >
      <div className="flex flex-col gap-3">
        {gateClosed ? (
          <div
            role="alert"
            className="flex flex-col gap-1 rounded border border-bad-line bg-bad-dim/50 px-3 py-2"
            data-testid="guaranteed-guard-write-gate"
          >
            <p className="m-0 text-label text-bad">
              {listReady ? t('guaranteed.guard.write_gate') : t('guaranteed.guard.load_failed')}
            </p>
            {listReady && gateReason.length > 0 ? (
              <p className="m-0 break-words font-mono text-dense text-bad">{gateReason}</p>
            ) : null}
          </div>
        ) : null}
        <div className="flex flex-wrap items-end gap-2">
          <label className="flex flex-col gap-1 text-label text-paper-low">
            {t('guaranteed.guard.scope')}
            <Select
              value={orgId ?? SKU_SCOPE}
              onValueChange={switchScope}
              options={[
                { value: SKU_SCOPE, label: t('guaranteed.guard.scope_sku') },
                ...orgOptions.map((entry) => ({ value: entry.orgId, label: entry.label })),
              ]}
              ariaLabel={t('guaranteed.guard.scope')}
              testId="guaranteed-guard-scope"
            />
          </label>
          <AddOrgPicker current={orgId} existing={new Set(orgOptions.map((entry) => entry.orgId))} onPick={switchScope} />
        </div>
        <p className="m-0 rounded border border-line bg-raised px-3 py-2 text-label text-paper-mid" data-testid="guaranteed-guard-division">
          {t('guaranteed.guard.division')}
        </p>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-label text-paper-low">
          <span>{t('guaranteed.guard.legend.off')}</span>
          <span className="text-warn">{t('guaranteed.guard.legend.shadow')}</span>
          <span className="text-ok">{t('guaranteed.guard.legend.act')}</span>
          <span>{t('guaranteed.guard.legend.parallel')}</span>
        </div>
        {draft.scope === 'sku' ? (
          <>
            <div className="grid grid-cols-[13rem_1fr_1fr_1fr] px-px text-label text-paper-low">
              <span className="px-3">{t('guaranteed.guard.col.signal')}</span>
              <span className="px-3">{t('guaranteed.guard.col.judge')}</span>
              <span className="px-3">{t('guaranteed.guard.col.walk')}</span>
              <span className="px-3">{t('guaranteed.guard.col.trip')}</span>
            </div>
            {GUARD_SIGNAL_VIEWS.map((view) => (
              <GuardSignalRow
                key={view.spec.kind}
                view={view}
                draft={draft.signals[view.spec.kind]}
                fromLegacy={view.spec.kind === 'first_content_stream' && streamFromLegacy(baseline, legacy)
                  && !changed.includes('first_content_stream.disposal')}
                issues={issues}
                stats={statsQuery.data?.[view.spec.kind]}
                statsState={statsState}
                disabled={disabled}
                onChange={(next) => setDraft((current) => (current.scope === 'sku'
                  ? { ...current, signals: { ...current.signals, [view.spec.kind]: next } }
                  : current))}
                onEnter={() => void save()}
              />
            ))}
            <p className="m-0 text-micro text-paper-low">{t('guaranteed.guard.stats.hits_gap')}</p>
            <GuardRecoverTables
              draft={draft}
              issues={issues}
              disabled={disabled}
              onChange={(next) => setDraft(next)}
            />
          </>
        ) : (
          <GuardOrgTable
            orgLabel={orgId === null ? '' : `${customerName(orgId)} · ${orgId}`}
            draft={draft}
            sku={skuView}
            issues={issues}
            disabled={disabled}
            onChange={(next) => setDraft(next)}
            onEnter={() => void save()}
          />
        )}
      </div>
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={t('guaranteed.guard.discard_title')}
        body={t('guaranteed.guard.discard_body', { n: changed.length })}
        confirmLabel={t('guaranteed.guard.discard_confirm')}
        cancelLabel={t('guaranteed.guard.discard_cancel')}
        closeLabel={t('guaranteed.guard.close')}
        tone="danger"
        testId="guaranteed-guard-discard"
        onConfirm={() => {
          setConfirmOpen(false);
          const next = pendingScope;
          setPendingScope(null);
          if (next === null) {
            onClose();
            return;
          }
          setDraft(baselineDraft);
          applyScope(next);
        }}
      />
    </Drawer>
  );
}

function orgChoices(
  orgGuards: readonly GuardConfigRow[],
  orgServiceRows: readonly GuaranteedServiceOrgSkuEntry[],
  customerName: (orgId: string) => string,
): readonly { readonly orgId: string; readonly label: string }[] {
  const ids = new Set<string>();
  for (const row of orgGuards) {
    if (row.org_id !== null) ids.add(row.org_id);
  }
  for (const row of orgServiceRows) ids.add(row.org_id);
  return [...ids].sort().map((id) => ({
    orgId: id,
    label: `${customerName(id)} · ${id}`,
  }));
}

function AddOrgPicker({
  current,
  existing,
  onPick,
}: {
  readonly current: string | null;
  readonly existing: ReadonlySet<string>;
  readonly onPick: (orgId: string) => void;
}): JSX.Element {
  const t = useT();
  const customersQuery = useQuotaCustomers({ staleTime: GUARANTEED_CUSTOMERS_STALE_MS });
  const customers = customersQuery.data?.customers ?? [];
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const directOrgId = isDirectOrgId(query) ? query.trim() : null;
  const matches = customers.filter((row) => {
    const q = query.trim().toLowerCase();
    if (q.length === 0) return false;
    if (existing.has(row.org_id)) return false;
    return row.org_name.toLowerCase().includes(q)
      || row.org_id.toLowerCase().includes(q)
      || row.email.toLowerCase().includes(q);
  }).slice(0, 20);
  const shown = picked !== null && current === picked
    ? (customers.find((row) => row.org_id === picked)?.org_name ?? picked)
    : query;

  return (
    <div ref={anchorRef} className="relative min-w-[16rem]">
      <FormField label={t('guaranteed.guard.add_org')} fieldId="guaranteed-guard-add-org">
        <Input
          id="guaranteed-guard-add-org"
          value={shown}
          onChange={(event) => {
            const next = event.target.value;
            setPicked(null);
            setQuery(next);
            setOpen(next.trim().length > 0);
          }}
          onFocus={() => { if (query.trim().length > 0 && picked === null) setOpen(true); }}
          placeholder={t('guaranteed.org_placeholder')}
          aria-label={t('guaranteed.guard.add_org')}
          data-testid="guaranteed-guard-add-org"
        />
      </FormField>
      {directOrgId !== null && picked === null && !existing.has(directOrgId) ? (
        <p className="m-0 mt-1 text-dense text-paper-low" data-testid="guaranteed-guard-org-direct">
          {t('guaranteed.org_direct')}
          <Button
            size="sm"
            variant="ghost"
            className="ml-2"
            data-testid="guaranteed-guard-org-direct-use"
            onClick={() => {
              setPicked(directOrgId);
              onPick(directOrgId);
              setOpen(false);
            }}
          >
            {t('guaranteed.guard.add_org')}
          </Button>
        </p>
      ) : null}
      <SearchDropdown
        open={open && picked === null && query.trim().length > 0}
        anchorRef={anchorRef}
        testId="guaranteed-guard-org-dropdown"
      >
        {matches.map((row) => (
          <button
            key={row.org_id}
            type="button"
            className="block w-full px-3 py-2 text-left hover:bg-active"
            data-testid={`guaranteed-guard-org-option-${row.org_id}`}
            onClick={() => {
              setPicked(row.org_id);
              setQuery(row.org_name);
              setOpen(false);
              onPick(row.org_id);
            }}
          >
            <span className="block text-dense text-paper">{row.org_name}</span>
            <span className="block font-mono text-micro text-paper-low">{row.email} · {row.org_id}</span>
          </button>
        ))}
      </SearchDropdown>
    </div>
  );
}
